import './theme.css';
import './app.css';
import { useState, useEffect } from 'react';
import JsonRenderer from './components/jsonrenderer';
import ActionButtons from './components/actionbuttons';
import FileSection from './components/filesection';
import Footer from './components/footer.js';
import AccessDenied from './components/accessdenied';
import AppHeader from './components/appheader';
import { Oval } from 'react-loader-spinner'
import { LoadingState, ErrorState } from './components/loadingerror';
import {
  useFetchData,
  useFetchFiles,
  handleSave,
  handleLoadBackup,
  runScheduler,
  useOverlay
} from './components/utils';
import config from './config';



const Notification = ({ notification, onClose, onSave, onRefresh }) => {
  if (notification.type === 'unsaved-changes') {
    return (
      <div className={`notification error`}>
        <span>{notification.message}</span>
        <div className="notification-buttons">
          <button onClick={onRefresh} className="btn-white">Refresh</button>
        </div>
        <button onClick={onClose} className="notification-close">×</button>
      </div>
    );
  }

  return (
    <div className={`notification ${notification.type}`}>
      <span>{notification.message}</span>
      <button onClick={onClose} className="notification-close">×</button>
    </div>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);

  
  const [changedFields, setChangedFields] = useState(new Set());
  const [isBackupCollapsed, setIsBackupCollapsed] = useState(true);
  const [isOutputCollapsed, setIsOutputCollapsed] = useState(true);
  const [notification, setNotification] = useState(null);
  const [currentBackupVersion, setCurrentBackupVersion] = useState(null);
  const [isLoadedFromBackup, setIsLoadedFromBackup] = useState(false);
  const [isSchedulerRunning, setIsSchedulerRunning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [coursesOptions, setCoursesOptions] = useState([]);
 
  const { jsonData, setJsonData, loading, error, isUnauthorized } = useFetchData(isAuthenticated);
  // Load the file lists after the config, so they don't hold up the first screen
  const { outputFiles, setOutputFiles, backupFiles, fetchBackupFiles } = useFetchFiles(isAuthenticated && !loading && !isUnauthorized);
  const showOverlay = useOverlay(isSchedulerRunning || isSaving);

  const appBaseUrl = config.SMA_SCHEDULER_APP_URL || config.BEST_TOOL_URL || window.location.origin;
  console.log("env", process.env.REACT_APP_SMA_SCHEDULER_SERVICE_BASE_URL, process.env.REACT_APP_SMA_SCHEDULER_MOCK_API_BASE_URL);
  // Fetch courses options based on toggle

  useEffect(() => {
    // 1. Check URL first, then SessionStorage
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    const storedToken = sessionStorage.getItem('token');
    const token = urlToken || storedToken;

    if (token) {
      // 2. No separate verification call: the backend verifies the token on every
      //    request, so the config load (useFetchData) doubles as the token check and
      //    a 401 there sends the user to Access Denied.
      sessionStorage.setItem('token', token);
      setIsAuthenticated(true);
      // Remove token from address bar for security
      window.history.replaceState({}, document.title, appBaseUrl);
    }
    setIsVerifying(false);
  }, [appBaseUrl]);

  const useStaticCourses = jsonData?.USE_STATIC_COURSES;

  // Update your existing fetchCourses to use the token
  useEffect(() => {
    const fetchCourses = async () => {
      const token = sessionStorage.getItem('token');
      if (!token || !isAuthenticated || useStaticCourses === undefined) return;

      try {
        const apiUrl = `${config.SMA_SCHEDULER_SERVICE_BASE_URL}/appointment-types`;

        const response = await fetch(apiUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log("Fetched courses:", data);
          const names = data.map(item => item.name).filter(Boolean);
          setCoursesOptions(names);
        }
      } catch (e) { console.error(e); }
    };
    fetchCourses();
  }, [useStaticCourses, isAuthenticated]);

  // Loading Screen
  if (isVerifying) return <LoadingState />;

  const redirectToLogin = () => {
    const portalUrl = config.BEST_TOOL_URL || window.location.origin;
    const currentApp = `${appBaseUrl}${window.location.pathname}`;
    window.location.assign(`${portalUrl}?redirect_uri=${encodeURIComponent(currentApp)}`);
  };

  // Logs out of SMA Scheduler only; the BEST Tools session is left untouched
  const handleLogout = () => {
    sessionStorage.removeItem('token');
    redirectToLogin();
  };

  // Unauthorized Screen
  if (!isAuthenticated || isUnauthorized) {
    return <AccessDenied onLogin={redirectToLogin} />;
  }
  

  const handleSaveFromNotification = async () => {
    await onHandleSave();
    setNotification(null);
    // After saving, refresh the page
    window.location.reload();
  };

  const handleRefreshFromNotification = () => {
    // Clear notification and proceed with refresh
    setNotification(null);
    // Allow the pending refresh to proceed
    window.location.reload();
  };

  const onHandleLoadBackup = async (filename) => {
    await handleLoadBackup(filename, setJsonData, setChangedFields, setIsLoadedFromBackup, setCurrentBackupVersion, setNotification, currentBackupVersion);
  };

  const onHandleSave = async () => {
    setIsSaving(true);
    await handleSave(jsonData, changedFields, setNotification, fetchBackupFiles, setChangedFields, setCurrentBackupVersion);
    setIsSaving(false);
  };

  const onRunScheduler = async () => {
    console.info("onRunScheduler called with currentBackupVersion:", currentBackupVersion);
    let versionForRun = currentBackupVersion;
    if (changedFields.size > 0) {
      setIsSaving(true);
      let savedVersion = null;
      try {
        savedVersion = await handleSave(
          jsonData,
          changedFields,
          setNotification,
          fetchBackupFiles,
          setChangedFields,
          setCurrentBackupVersion,
          { notifyOnNoChanges: false }
        );
      } finally {
        setIsSaving(false);
      }
      if (!savedVersion) {
        return;
      }
      versionForRun = savedVersion;
    }
    await runScheduler(
      jsonData,
      setNotification,
      setOutputFiles,
      setJsonData,
      setChangedFields,
      setIsLoadedFromBackup,
      versionForRun,
      setIsSchedulerRunning,
      fetchBackupFiles,
      setCurrentBackupVersion
    );
  };
  
  const getChangedFields = (newData, oldData) => {
    const changed = new Set();
    const compareObjects = (newObj, oldObj, path = '') => {
      if (typeof newObj !== 'object' || newObj === null || typeof oldObj !== 'object' || oldObj === null) {
        if (JSON.stringify(newObj) !== JSON.stringify(oldObj)) {
          changed.add(path);
        }
        return;
      }
      const allKeys = new Set([...Object.keys(newObj || {}), ...Object.keys(oldObj || {})]);
      for (const key of allKeys) {
        compareObjects(newObj[key], oldObj[key], path ? `${path}.${key}` : key);
      }
    };
    compareObjects(newData, oldData);
    return changed;
  };
  // Show loading state
  if (loading) {
    return <LoadingState />;
  }

  // Show error state
  if (error) {
    return <ErrorState error={error} />;
  }
  return (
    <div className="App">
      <AppHeader title="SMA Scheduler" onLogout={handleLogout} />
      {notification && (
        <Notification
          notification={notification}
          onClose={() => setNotification(null)}
          onSave={handleSaveFromNotification}
          onRefresh={handleRefreshFromNotification}
        />
      )}
      <JsonRenderer data={jsonData} onDataChange={(newData) => {
        setJsonData(newData);
        setChangedFields(new Set([...changedFields, ...getChangedFields(newData, jsonData)]));
      }} changedFields={isLoadedFromBackup ? new Set() : changedFields} setNotification={setNotification} currentBackupVersion={currentBackupVersion} isLoadedFromBackup={isLoadedFromBackup} coursesOptions={coursesOptions} />
      <ActionButtons onSave={onHandleSave} onRunScheduler={onRunScheduler} isSchedulerRunning={isSchedulerRunning} />
      {outputFiles && outputFiles.length > 0 && (
        <FileSection
          title="Schedule Files"
          isCollapsed={isOutputCollapsed}
          onToggleCollapse={() => setIsOutputCollapsed(!isOutputCollapsed)}
          files={outputFiles}
          driveLinkKey="driveLink"
          modifiedTimeKey="modifiedTime"
          nameKey="name"
        />
      )}
      <FileSection
        title="Config Files Backup"
        isCollapsed={isBackupCollapsed}
        onToggleCollapse={() => setIsBackupCollapsed(!isBackupCollapsed)}
        files={backupFiles}
        onLoadBackup={onHandleLoadBackup}
      />
      
      {showOverlay && (
        <div className="scheduler-overlay">
          <Oval
          height={50}
          width={50}
          color="#4fa94d"
          visible={true}
          ariaLabel="oval-loading"
          secondaryColor="#4fa94d"
          strokeWidth={3}
          strokeWidthSecondary={2}
        />
        </div>
      )}
      
      <Footer />
    </div>
  );
}
export default App;
